import { EditorialPageHero } from "@/components/sections/EditorialPageHero";
import { Layout } from "@/components/layout/Layout";
import { QuickAnswer } from "@/components/shared/QuickAnswer";
import { PeopleAlsoAsk } from "@/components/shared/PeopleAlsoAsk";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/router-compat";
import { aiEvidences, integrationFaqs, integrationSteps } from "@/data/ia-evidencias";

const IntegracionIaSeo = () => (
  <Layout>
    <EditorialPageHero
      kicker="Especialización · IA aplicada al SEO"
      eyebrow="SEO y GEO"
      title={<>Integración de IA en el SEO.</>}
      highlight="Para que Google y los asistentes entiendan tu negocio."
      lead="Unimos SEO técnico, Google Maps y GEO en un mismo trabajo, con evidencias reales de apariciones en respuestas de IA."
      description="Usamos IA para analizar, producir y revisar más rápido. Las decisiones, la exactitud y la medición las controla el equipo."
      primaryCta={{ label: "Solicita un diagnóstico de tu empresa", href: "/agenda-consultoria" }}
      secondaryCta={{ label: "Ver evidencias", href: "#evidencias" }}
      video={false}
    />

    <QuickAnswer
      question="¿Qué significa integrar IA en el SEO de una empresa?"
      answer="Es aplicar IA en dos sentidos: como herramienta del equipo (análisis, contenido y revisión) y como canal de visibilidad (respuestas de Google AI Overviews, AI Mode, ChatGPT o Gemini). La base sigue siendo SEO accesible, contenido útil y datos coherentes del negocio; ninguna agencia puede garantizar citas o recomendaciones."
      ctaText="Ver servicio GEO"
      ctaLink="/servicios/geo-seo-360"
    />

    <section id="evidencias" className="py-20 bg-seo-dark scroll-mt-24">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-white">
          Proyectos que aparecen en respuestas de IA
        </h2>
        <p className="text-center text-gray-300 text-lg mb-12 max-w-3xl mx-auto">
          Capturas reales de respuestas de IA de Google, tomadas el {"5 de noviembre de 2026"}. Cada una refleja una consulta y un momento concretos: las respuestas cambian según la búsqueda, la ubicación y el día.
        </p>
        <div className="grid gap-8 md:grid-cols-2">
          {aiEvidences.map((ev) => (
            <article key={ev.id} className="rounded-lg border border-seo-blue/20 bg-seo-darkBlue/30 p-6">
              <p className="text-sm uppercase tracking-wide text-seo-bright mb-2">{ev.kind}</p>
              <h3 className="text-xl font-bold text-white mb-2">{ev.name}</h3>
              <p className="text-gray-300 mb-4">{ev.summary}</p>
              <img
                src={ev.image.url}
                alt={ev.alt}
                width={ev.width}
                height={ev.height}
                loading="lazy"
                decoding="async"
                className="w-full h-auto rounded-md border border-seo-blue/20 bg-white"
              />
              <p className="text-xs text-gray-400 mt-3">
                Consulta: «{ev.query}» · Captura del 5 nov 2026
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 bg-seo-darkBlue/20">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">Cómo lo trabajamos</h2>
        <ol className="grid gap-6 md:grid-cols-2">
          {integrationSteps.map((s, i) => (
            <li key={s.title} className="rounded-lg border border-seo-blue/20 bg-seo-dark p-6">
              <h3 className="text-lg font-bold text-white mb-2">{i + 1}. {s.title}</h3>
              <p className="text-gray-300">{s.text}</p>
            </li>
          ))}
        </ol>
        <p className="text-gray-400 text-sm mt-8 text-center max-w-3xl mx-auto">
          Google indica que no hay requisitos especiales para aparecer en sus funciones de IA más allá de las buenas prácticas SEO.{" "}
          <a href="https://developers.google.com/search/docs/appearance/ai-features" target="_blank" rel="noopener noreferrer" className="underline text-seo-bright">
            Documentación oficial de Google
          </a>
          .
        </p>
        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <Button asChild size="lg"><Link to="/agenda-consultoria">Solicita un diagnóstico de tu empresa</Link></Button>
          <Button asChild size="lg" variant="outline"><Link to="/servicios/seo-local-ia">SEO local y Google Maps</Link></Button>
        </div>
      </div>
    </section>

    <PeopleAlsoAsk questions={integrationFaqs} />
  </Layout>
);

export default IntegracionIaSeo;
